import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import { useGlobalMDAlert } from "context";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CancelarPrestamoDialog from "components/prestamos/CancelarPrestamoDialog";
import { useApi } from "lib/api";

export default function CancelacionPrestamos() {
  const { showAlert } = useGlobalMDAlert();
  const { post: postPrestamos } = useApi();

  const [opciones, setOpciones] = useState([]);
  const [sel, setSel] = useState(null);
  const [loadingOpciones, setLoadingOpciones] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const searchTimer = useRef(null);

  // El préstamo personal no tiene motor: no se pregunta por el chasis.
  const chasisSel = String(sel?.producto_chasis || sel?.chasis_prestamo || "").trim();

  useEffect(() => {
    return () => clearTimeout(searchTimer.current);
  }, []);

  const buscarPrestamos = async (q) => {
    setLoadingOpciones(true);
    try {
      const { ok, data } = await postPrestamos("/api/prestamosbackend_buscaractivos", { q, limit: 50 });
      console.log(data);
      if (ok && Array.isArray(data.prestamos)) setOpciones(data.prestamos);
      else
        showAlert({
          message: data.Error || "No se pudo cargar la lista de préstamos",
          color: "error",
        });
    } catch (e) {
      console.error(e);
      showAlert({
        message:
          e && typeof e.message === "string" && e.message.includes("Failed to fetch")
            ? "Error de red (¿servidor en marcha?)"
            : "Error al buscar préstamos",
        color: "error",
      });
    } finally {
      setLoadingOpciones(false);
    }
  };

  const cancelarPrestamo = async (devolver) => {
    if (!sel) return;
    const noprest = sel.noprest ?? sel.NOPREST;
    setCancelando(true);
    try {
      const { ok, data } = await postPrestamos("/api/prestamosbackend_cancelarprestamo", {
        noprest,
        devolverinventario: devolver ? "S" : "N",
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Operación completada", color: "success", autoHideDuration: 8000 });
        setOpciones((prev) => prev.filter((p) => String(p.noprest ?? p.NOPREST) !== String(noprest)));
        setSel(null);
        setConfirmOpen(false);
      } else showAlert({ message: data.Error || "Error", color: "error" });
    } catch (e) {
      console.error(e);
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setCancelando(false);
    }
  };

  const fmtMoney = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    if (Number.isNaN(n)) return String(v);
    return `RD$ ${n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Box
      component="main"
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={{ xs: 1.5, sm: 0 }}
            mb={2}
          >
            <HighlightOffIcon
              sx={{
                fontSize: { xs: 28, sm: 32 },
                color: "primary.main",
                mr: { xs: 0, sm: 2 },
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              color="primary"
              sx={{
                fontSize: { xs: "1.35rem", sm: "2rem", md: "2.125rem" },
                lineHeight: { xs: 1.35, sm: 1.2 },
                wordBreak: "break-word",
              }}
            >
              Cancelación de préstamos
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
            Busque por número o nombre, seleccione un préstamo y confirme la cancelación.
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mb: 2, fontWeight: 600, fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Selección de préstamo
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={12} md={8}>
              <Autocomplete
                fullWidth
                disablePortal
                options={opciones}
                loading={loadingOpciones}
                filterOptions={(x) => x}
                onOpen={() => buscarPrestamos("")}
                onInputChange={(_, value, reason) => {
                  if (reason !== "input") return;
                  clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(() => buscarPrestamos(value.trim()), 280);
                }}
                getOptionLabel={(o) =>
                  o && typeof o === "object"
                    ? `${o.noprest ?? o.NOPREST ?? ""} — ${String(o.cliente ?? o.CLIENTE ?? "").trim()}`.trim()
                    : ""
                }
                isOptionEqualToValue={(a, b) =>
                  a != null && b != null && String(a.noprest ?? a.NOPREST) === String(b.noprest ?? b.NOPREST)
                }
                value={sel}
                onChange={async (_, opt) => {
                  if (!opt) {
                    setSel(null);
                    return;
                  }
                  try {
                    const id = opt.noprest ?? opt.NOPREST;
                    const { ok, data } = await postPrestamos("/api/prestamosbackend_detalleactivo", { noprest: id });
                    if (ok && data.prestamo) setSel(data.prestamo);
                    else {
                      showAlert({ message: data.Error || "No se pudo cargar el préstamo", color: "error" });
                      setSel(null);
                    }
                  } catch (e) {
                    console.error(e);
                    showAlert({ message: "Error de red al cargar el préstamo", color: "error" });
                    setSel(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo activo"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { minHeight: 45 } }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {sel &&  (
        <>
          <Grid container spacing={{ xs: 2, sm: 2 }} sx={{ mb: 3 }}>
            {[
              ["Estado", sel.status || "—"],
              ["Fecha del préstamo", sel.fecha_prestamo || "—"],
              ["Último pago", sel.fecha_ultimo_pago || sel.FECHA_ULTIMO_PAGO || "—"],
              ["Sucursal (concesión)", sel.sucursal_concesion || sel.SUCURSAL_CONCESION || "—"],
              ["Interés pendiente", fmtMoney(sel.balance_interes)],
              ["Capital pendiente", fmtMoney(sel.balance_capital)],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                  <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ wordBreak: "break-word", fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                      {value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent sx={{ "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mb: 2, fontWeight: 600, fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                Garantía / producto
              </Typography>
              {sel.producto_chasis || sel.producto_marca || sel.producto_modelo ? (
                <Grid container spacing={{ xs: 2, sm: 2 }}>
                  {[
                    ["Chasis", sel.producto_chasis],
                    ["Marca", sel.producto_marca],
                    ["Modelo", sel.producto_modelo],
                    ["Color", sel.producto_color],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={3} key={label}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {value || "—"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                  {String(sel.chasis_prestamo || "").trim() || "Sin detalle de garantía"}
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Box display="flex" justifyContent={{ xs: "stretch", sm: "flex-end" }}>
        <Button
          variant="contained"
          color="error"
          disabled={!sel}
          startIcon={<HighlightOffIcon />}
          onClick={() => setConfirmOpen(true)}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: 48, sm: 42 },
            boxShadow: 2,
            "&:hover": { boxShadow: 4 },
          }}
        >
          Cancelar préstamo
        </Button>
      </Box>

      <CancelarPrestamoDialog
        abierto={confirmOpen}
        noprest={sel?.noprest ?? sel?.NOPREST}
        cliente={sel?.cliente ?? sel?.CLIENTE}
        chasis={chasisSel}
        tieneChasis={Boolean(chasisSel)}
        cargando={cancelando}
        onCerrar={() => setConfirmOpen(false)}
        onConfirmar={cancelarPrestamo}
      />
    </Box>
  );
}
